import {
  type StartedWorkSetting as StartedWork,
  startedWorkSettingSchema,
} from '@crowdex/agent-contracts/api';
import { apiFetch } from '@crowdex/host-kit/renderer/http';
import {
  ChoiceGroup,
  ChoiceItem,
  SegmentedControl,
  SegmentedControlItem,
} from '@crowdex/ui/components/segmented-control';
import { cn } from '@crowdex/ui/lib/utils';
import { ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import { AppearanceThemeSettings } from '@/components/settings-page/appearance-theme-settings';
import { DesktopDownloadSettings } from '@/components/settings-page/desktop-download-settings';
import { DesktopUpdateSettings } from '@/components/settings-page/desktop-update-settings';
import { FilesSettings } from '@/components/settings-page/files-settings';
import {
  SettingLinkRow,
  SettingRow,
  SettingSelect,
  SettingsGroup,
  SettingsNote,
  SettingToggle,
} from '@/components/settings-page/setting-controls';
import {
  setPipAlwaysHidden,
  usePipTaskState,
} from '@/features/picture-in-picture/picture-in-picture-host';
import type { AppSettings } from '@/lib/app-settings';
import { electronInvoke, isElectronDesktop } from '@/lib/electron-bridge';
import { useRouter } from '@/lib/navigation';
import { settingsSectionPath } from '@/lib/settings-page/sections';
import { useAppPath } from '@/vite/navigation-store';

type Update = <K extends keyof AppSettings>(key: K, value: AppSettings[K]) => void;

const THEMES = [
  { value: 'system', label: 'System' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
] as const;

const LANGUAGES = [
  { value: 'system', label: 'System default' },
  { value: 'en', label: 'English' },
  { value: 'fr', label: 'Français' },
];

const EDITORS = [
  { value: 'vscode', label: 'VS Code' },
  { value: 'cursor', label: 'Cursor' },
  { value: 'zed', label: 'Zed' },
  { value: 'system', label: 'System default' },
];

/** Kept by the Desktop, which shows the stack: hiding it everywhere is "Hide for all active chats". */
function PictureInPictureSetting() {
  const { alwaysHidden } = usePipTaskState();
  return (
    <SettingRow
      description="Keeps Live and the browser agents drive in a small window when you switch apps"
      title="Picture-in-picture for calls"
    >
      <SettingToggle
        checked={!alwaysHidden}
        label="Picture-in-picture for calls"
        onChange={(value) => setPipAlwaysHidden(!value)}
      />
    </SettingRow>
  );
}

const STARTED_WORK_OPTIONS = [
  { value: 'allow-all', label: 'Full access' },
  { value: 'allow-edits', label: 'Edit files' },
  { value: 'allow-reads', label: 'Read only' },
];

/**
 * What conversations started for the user (by their assistant, a project's lead, a task, an
 * app) may do without asking them, at most: kept on the server, for every device.
 */
function StartedWorkSetting() {
  const [mode, setMode] = useState<StartedWork['permissionMode'] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    apiFetch<StartedWork>('/api/settings/started-work').then(
      (answer) => {
        const parsed = startedWorkSettingSchema.safeParse(answer);
        if (live && parsed.success) setMode(parsed.data.permissionMode);
      },
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, []);
  if (mode === null) return null;
  return (
    <SettingRow
      description={
        error ??
        'What the conversations your assistant, your projects and apps start for you may do without asking'
      }
      title="Work started for you"
    >
      <SettingSelect
        label="Work started for you"
        onValueChange={(value) => {
          const next = startedWorkSettingSchema.shape.permissionMode.parse(value);
          setMode(next);
          setError(null);
          void apiFetch('/api/settings/started-work', {
            method: 'PUT',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ permissionMode: next } satisfies StartedWork),
          }).catch((reason: unknown) =>
            setError(reason instanceof Error ? reason.message : String(reason)),
          );
        }}
        options={STARTED_WORK_OPTIONS}
        value={mode}
      />
    </SettingRow>
  );
}

/** Whether the Desktop opens when the user logs in (the system's login items). */
function OpenAtLoginSetting() {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  useEffect(() => {
    void electronInvoke<boolean>('login_item_get').then(
      (value) => setEnabled(value === true),
      () => setEnabled(null),
    );
  }, []);
  if (enabled === null) return null;
  return (
    <SettingRow title="Open at login">
      <SettingToggle
        checked={enabled}
        label="Open at login"
        onChange={(value) => {
          setEnabled(value);
          void electronInvoke('login_item_set', { openAtLogin: value }).catch(() =>
            setEnabled(!value),
          );
        }}
      />
    </SettingRow>
  );
}

/** The phone's theme choice: a small picture of each. */
function ThemeTiles({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return (
    <ChoiceGroup
      aria-label="Theme"
      className="grid grid-cols-3 gap-2.5 px-3.5 pt-3.5 pb-3"
      onValueChange={onChange}
      value={value}
    >
      {THEMES.map((theme) => {
        const selected = value === theme.value;
        return (
          <ChoiceItem
            aria-label={theme.label}
            className="flex flex-col items-center gap-2 text-sm"
            key={theme.value}
            value={theme.value}
          >
            <span
              className={cn(
                'flex h-20 w-full overflow-hidden rounded-lg border',
                selected ? 'border-primary ring-2 ring-primary' : 'border-hairline-strong',
              )}
            >
              {theme.value !== 'dark' && (
                <span className="flex-1 bg-background" data-theme="desktop-light" />
              )}
              {theme.value !== 'light' && (
                <span className="flex-1 bg-background" data-theme="desktop-dark" />
              )}
            </span>
            <span className={selected ? 'font-semibold text-primary' : 'text-muted-foreground'}>
              {theme.label}
            </span>
          </ChoiceItem>
        );
      })}
    </ChoiceGroup>
  );
}

/** Settings › General › Themes and fonts: an address of its own, so a phone's back returns to General. */
export const THEMES_PATH = `${settingsSectionPath('general')}&view=themes`;

export function isThemesPath(path: string): boolean {
  const url = new URL(path, 'https://crowdex.invalid');
  return (
    url.pathname === '/settings' &&
    url.searchParams.get('section') === 'general' &&
    url.searchParams.get('view') === 'themes'
  );
}

/** Settings › General (D16, phone 14). The appearance editor sits one level down. */
export function GeneralSettingsSection({
  preferences,
  onUpdate,
  onOpenAgents,
  phone = false,
}: {
  preferences: AppSettings;
  onUpdate: Update;
  /** The default agent is chosen in Settings › Agents. */
  onOpenAgents: () => void;
  phone?: boolean;
}) {
  const router = useRouter();
  const advanced = isThemesPath(useAppPath());
  const desktop = isElectronDesktop();
  if (advanced)
    return (
      <>
        {/* A phone goes up with its header's back, like from a plugin's page. */}
        {!phone && (
          <button
            className="mb-5 flex items-center gap-1.5 text-muted-foreground text-sm hover:text-foreground"
            onClick={() => router.replace(settingsSectionPath('general'))}
            type="button"
          >
            <ArrowLeft className="size-4" /> General
          </button>
        )}
        <h2 className="type-card-title mb-5">Themes and fonts</h2>
        <AppearanceThemeSettings
          appearance={preferences.appearance}
          onChange={(appearance) => onUpdate('appearance', appearance)}
        />
      </>
    );
  return (
    <>
      <SettingsGroup title="Appearance">
        {phone ? (
          <ThemeTiles onChange={(value) => onUpdate('theme', value)} value={preferences.theme} />
        ) : (
          <SettingRow title="Theme">
            <SegmentedControl
              aria-label="Theme"
              onValueChange={(value) => onUpdate('theme', value)}
              value={preferences.theme}
            >
              {THEMES.map((theme) => (
                <SegmentedControlItem key={theme.value} value={theme.value}>
                  {theme.label}
                </SegmentedControlItem>
              ))}
            </SegmentedControl>
          </SettingRow>
        )}
        <SettingRow description="Tighter spacing to fit more on screen" title="Compact interface">
          <SettingToggle
            checked={preferences.compactMode}
            label="Compact interface"
            onChange={(value) => onUpdate('compactMode', value)}
          />
        </SettingRow>
        <SettingLinkRow
          description="The code theme, colours and fonts of the light and dark variants"
          // The phone pushes a sub-page (its back returns to General), as for a section.
          onSelect={() => (phone ? router.push(THEMES_PATH) : router.replace(THEMES_PATH))}
          title="Themes and fonts"
        />
      </SettingsGroup>
      <SettingsGroup title="Agent defaults">
        {/* One place for the default agent: Settings › Agents ("Make default"). */}
        <SettingLinkRow
          description="The default agent and the runtimes are chosen in Settings › Agents"
          onSelect={onOpenAgents}
          title="Agents"
        />
        <StartedWorkSetting />
        <SettingRow description="Agents run commands without asking" title="Auto-approve actions">
          <SettingToggle
            checked={preferences.autoApprove}
            label="Auto-approve actions"
            onChange={(value) => onUpdate('autoApprove', value)}
          />
        </SettingRow>
        <SettingRow description="Suggests your next message; Tab uses it" title="Show predictions">
          <SettingToggle
            checked={preferences.messagePredictions}
            label="Show predictions"
            onChange={(value) => onUpdate('messagePredictions', value)}
          />
        </SettingRow>
      </SettingsGroup>
      <SettingsGroup title="Pull requests">
        <SettingRow title="Create as drafts">
          <SettingToggle
            checked={preferences.draftPullRequests}
            label="Create as drafts"
            onChange={(value) => onUpdate('draftPullRequests', value)}
          />
        </SettingRow>
        <SettingRow
          description="Its checks next to the conversation's title"
          title="Show the pull request in the header"
        >
          <SettingToggle
            checked={preferences.pullRequestStatusInHeader}
            label="Show the pull request in the header"
            onChange={(value) => onUpdate('pullRequestStatusInHeader', value)}
          />
        </SettingRow>
      </SettingsGroup>
      {phone ? (
        <>
          <SettingsGroup title="Language">
            <SettingRow title="Language">
              <SettingSelect
                label="Language"
                onValueChange={(value) => onUpdate('language', value)}
                options={LANGUAGES}
                value={preferences.language}
              />
            </SettingRow>
          </SettingsGroup>
          <SettingsNote>
            Desktop-only settings (editor, autosave, picture-in-picture) are hidden on this device.
          </SettingsNote>
        </>
      ) : (
        <SettingsGroup title="This computer">
          <SettingRow title="Preferred editor">
            <SettingSelect
              label="Preferred editor"
              onValueChange={(value) => onUpdate('editor', value)}
              options={EDITORS}
              value={preferences.editor}
            />
          </SettingRow>
          {desktop && <OpenAtLoginSetting />}
          {desktop && <PictureInPictureSetting />}
          <SettingRow description="Conversation drafts and interface changes" title="Autosave">
            <SettingToggle
              checked={preferences.autoSave}
              label="Autosave"
              onChange={(value) => onUpdate('autoSave', value)}
            />
          </SettingRow>
          <SettingRow title="Language">
            <SettingSelect
              label="Language"
              onValueChange={(value) => onUpdate('language', value)}
              options={LANGUAGES}
              value={preferences.language}
            />
          </SettingRow>
          {desktop ? <DesktopUpdateSettings /> : <DesktopDownloadSettings />}
        </SettingsGroup>
      )}
      <FilesSettings />
    </>
  );
}
